import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DownOutlined } from '@ant-design/icons';
import { Alert, Button, Dropdown, Empty, Pagination, Tabs } from 'antd';
import type { MenuProps } from 'antd';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import ComponentBlocker from '@src/components/ComponentBlocker';
import FilterActiveTags from '@src/components/FilterActiveTags/FilterActiveTags';
import FilterContainer from '@src/components/FilterContainer';
import type { FilterContainerActionButton } from '@src/components/FilterContainer/FilterContainer.interfaces';
import type { FilterValues } from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import type { AttendanceDashboardItem } from '@src/models/interfaces/attendanceDashboard.interface';
import { ATTENDANCE_LEAVE_TYPE } from '@src/models/interfaces/attendanceLeaveType';
import type { IWorkJourneyManagementUser } from '@src/models/interfaces/workJourneyManagement.interface';
import ListViewportLayout from '@src/components/ListViewportLayout';
import SecondaryPanel from '@src/components/SecondaryPanel/SecondaryPanel';
import { ResponsiveTable } from '@src/components/Table';
import { ALLOWED_ROLES } from '@src/packages/utils/access';
import { DateUtil } from '@src/packages/utils/dates';
import IntlMessages from '@src/util/IntlMessages';

import styles from './WorkJourneyRealTime.module.less';

import {
  WORK_JOURNEY_REAL_TIME_TAB,
  type WorkJourneyRealTimeTab,
} from '@src/packages/workJourney/constants';

import MarkAttendanceDrawer from './components/MarkAttendanceDrawer';
import MarkAttendanceConfirmModal from './components/MarkAttendanceDrawer/components/MarkAttendanceConfirmModal';
import { useStartJourneyConfirm } from './components/MarkAttendanceDrawer/hooks/useStartJourneyConfirm';
import type { MarkAttendanceUserRow } from './components/MarkAttendanceDrawer/types';
import { toMarkAttendanceJourneyDetailsFromDashboard } from './components/MarkAttendanceDrawer/utils/toMarkAttendanceJourneyDetailsFromDashboard';
import { buildJourneyNameByEmployeeId } from './components/MarkAttendanceDrawer/utils/buildJourneyNameByEmployeeId';
import {
  MarkedJourneyUsersDrawer,
  type MarkedJourneyUsersDrawerVariant,
} from './components/MarkedJourneyUsersDrawer';
import { getCoverPickerRowKey } from '../WorkJourneyManagement/components/CoverPositionDrawers/coverPickerRowKey';
import { WorkJourneyCoverAbsenceDrawer } from '../WorkJourneyManagement/components/CoverPositionDrawers/WorkJourneyCoverAbsenceDrawer';
import { WorkJourneyCoverAbsenceReplaceDrawer } from '../WorkJourneyManagement/components/CoverPositionDrawers/WorkJourneyCoverAbsenceReplaceDrawer';
import { WorkJourneyMatrixAbsenceDrawer } from '../WorkJourneyMonthMatrix/components/WorkJourneyMatrixAbsenceDrawer';
import WorkJourneyRealTimeFilters from './components/WorkJourneyRealTimeFilters';
import WorkJourneyRealTimeKpiCards from './components/WorkJourneyRealTimeKpiCards';
import {
  type ReportAbsencePayload,
  useWorkJourneyRealTime,
} from './hooks/useWorkJourneyRealTime';
import type {
  AttendanceStatusDisplay,
  JourneyAttendanceGroupRow,
} from './utils/attendanceStatus/types';
import { hasAttendanceJourneyStarted, toUserStatusInput } from './utils/attendanceMetrics';
import {
  isUserPendingMark,
  resolveUserRowAttendanceState,
} from './utils/attendanceStatus/resolveAttendanceStatus';
import { INIT_WORK_JOURNEY_REAL_TIME_FILTERS } from './utils/initRealTimeFilters';
import { resolveInitialRealTimeKpi } from './utils/bootstrapRealTimeKpiFromLocation';
import { menuItemIcon } from './utils/menuItemIcon';
import { createAttendanceStatusRenderer } from './utils/renderAttendanceStatusTag';
import type { WorkJourneyRealTimeLocationState } from './locationState';

const toMarkAttendanceUserRow = (item: AttendanceDashboardItem): MarkAttendanceUserRow => ({
  id: item.employeeId,
  firstName: item.userFirstName ?? undefined,
  lastName: item.userLastName ?? undefined,
  fullName: item.userDisplayName !== '—' ? item.userDisplayName : undefined,
  idCard: item.userIdCard ?? undefined,
  journeyName: item.journeyName ?? null,
  info: {
    workJourneyConfigId: item.workJourneyConfigId ?? undefined,
    workShiftId: item.workShiftId ?? undefined,
  },
});

const toMatrixUser = (item: AttendanceDashboardItem) => ({
  id: item.employeeId,
  displayName: item.userDisplayName,
  idCard: item.userIdCard,
  programmedHours: 0,
});

const EMPTY_USER_ROWS: AttendanceDashboardItem[] = [];
const EMPTY_JOURNEY_ROWS: JourneyAttendanceGroupRow[] = [];

const REAL_TIME_ACTIONS = {
  REPORT_ABSENCE: 'reportAbsence',
  REPLACE_WORKER: 'replaceWorker',
  MARK_ATTENDANCE: 'markAttendance',
} as const;

const REAL_TIME_TAB_ITEMS = [
  {
    key: WORK_JOURNEY_REAL_TIME_TAB.BY_JOURNEY,
    label: <IntlMessages id="workJourney.realTime.tab.byJourney" />,
  },
  {
    key: WORK_JOURNEY_REAL_TIME_TAB.BY_USER,
    label: <IntlMessages id="workJourney.realTime.tab.byUser" />,
  },
];

const WorkJourneyRealTime: React.FC = () => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation<WorkJourneyRealTimeLocationState>();
  const bootstrappedKpiRef = useRef(false);
  const [markAttendanceOpen, setMarkAttendanceOpen] = useState(false);
  const [markInitialItem, setMarkInitialItem] = useState<AttendanceDashboardItem | null>(null);
  const [journeyUsers, setJourneyUsers] = useState<MarkAttendanceUserRow[] | null>(null);
  const [markedJourneyUsers, setMarkedJourneyUsers] = useState<AttendanceDashboardItem[] | null>(
    null,
  );
  const [markedJourneyUsersVariant, setMarkedJourneyUsersVariant] =
    useState<MarkedJourneyUsersDrawerVariant>('roster');
  const [replaceListOpen, setReplaceListOpen] = useState(false);
  const [replacementTargetRecordId, setReplacementTargetRecordId] = useState<string | null>(null);
  const [reportAbsencePickerOpen, setReportAbsencePickerOpen] = useState(false);
  const [reportAbsenceUser, setReportAbsenceUser] = useState<AttendanceDashboardItem | null>(null);
  const [reportAbsenceSubmitting, setReportAbsenceSubmitting] = useState(false);

  const refreshAfterStartRef = useRef<() => void>(() => {});
  const {
    open: startJourneyConfirmOpen,
    confirmKind: startJourneyConfirmKind,
    userDisplayName: startJourneyUserDisplayName,
    markedAt: startJourneyMarkedAt,
    journeyDetails: startJourneyDetails,
    submitting: startJourneySubmitting,
    openForUser: openStartJourneyConfirm,
    handleConfirm: handleConfirmStartJourney,
    handleCancel: handleCancelStartJourney,
    handleMarkedAtChange: handleStartJourneyMarkedAtChange,
  } = useStartJourneyConfirm({
    onSuccess: () => refreshAfterStartRef.current(),
  });

  const handleMarkUser = useCallback((item: AttendanceDashboardItem) => {
    if (!hasAttendanceJourneyStarted(item)) {
      openStartJourneyConfirm(
        toMarkAttendanceUserRow(item),
        toMarkAttendanceJourneyDetailsFromDashboard(item),
      );
      return;
    }

    setMarkInitialItem(item);
    setJourneyUsers(null);
    setMarkAttendanceOpen(true);
  }, [openStartJourneyConfirm]);

  const handleStartJourneyFromPicker = useCallback(
    (user: MarkAttendanceUserRow) => {
      setMarkAttendanceOpen(false);
      setMarkInitialItem(null);
      setJourneyUsers(null);
      openStartJourneyConfirm(user);
    },
    [openStartJourneyConfirm],
  );

  const handleViewJourneyUsers = useCallback((items: AttendanceDashboardItem[]) => {
    setJourneyUsers(items.map(toMarkAttendanceUserRow));
    setMarkInitialItem(null);
    setMarkAttendanceOpen(true);
  }, []);

  const handleViewMarkedJourneyUsers = useCallback((items: AttendanceDashboardItem[]) => {
    setMarkedJourneyUsersVariant('roster');
    setMarkedJourneyUsers(items);
  }, []);

  const handleCloseMarkedJourneyUsers = useCallback(() => {
    setMarkedJourneyUsers(null);
    setMarkedJourneyUsersVariant('roster');
  }, []);

  const handleViewMarkedUserDetail = useCallback(
    (item: AttendanceDashboardItem) => {
      setMarkedJourneyUsers(null);
      setMarkedJourneyUsersVariant('roster');
      handleMarkUser(item);
    },
    [handleMarkUser],
  );

  const handleReplaceUser = useCallback((item: AttendanceDashboardItem) => {
    setReplacementTargetRecordId(item.id);
  }, []);

  const handleReportAbsenceForUser = useCallback((item: AttendanceDashboardItem) => {
    setReportAbsencePickerOpen(false);
    setReportAbsenceUser(item);
  }, []);

  const {
    loading,
    isLoaded,
    error,
    isRangeTruncated,
    rangeTotalAvailable,
    rangeMaxRecords,
    activeTab,
    setActiveTab,
    currentPage,
    pageSize,
    totalCount,
    handlePageChange,
    kpis,
    activeKpiKey,
    handleSelectKpi,
    userColumns,
    journeyColumns,
    userTableData,
    filteredItems,
    dayItems,
    journeyTableData,
    toggleFilters,
    isFilterPanelOpen,
    filters,
    setFilters,
    handleStructuralFilterChange,
    shiftOptions,
    journeyOptions,
    userOptions,
    replacementTargets,
    reportAbsenceUsers,
    handleReportAbsence,
    refresh,
  } = useWorkJourneyRealTime({
    onMarkUser: handleMarkUser,
    onViewJourneyUsers: handleViewJourneyUsers,
    onViewMarkedJourneyUsers: handleViewMarkedJourneyUsers,
    onReplaceUser: handleReplaceUser,
    onReportAbsence: handleReportAbsenceForUser,
  });

  const handleOpenGeneralFix = useCallback(() => {
    setMarkedJourneyUsersVariant('fix');
    // Needs mark/replace: no start mark yet, excluding reported leaves only
    // (computed ABSENT past 30′ still belongs here for "Requieren acción").
    setMarkedJourneyUsers(
      filteredItems.filter(item => isUserPendingMark(toUserStatusInput(item))),
    );
  }, [filteredItems]);
  refreshAfterStartRef.current = refresh;

  useEffect(() => {
    if (bootstrappedKpiRef.current) {
      return;
    }

    const initialKpi = resolveInitialRealTimeKpi(location);
    if (!initialKpi) {
      return;
    }

    bootstrappedKpiRef.current = true;
    handleSelectKpi(initialKpi);
    history.replace({ pathname: location.pathname, search: location.search, state: undefined });
  }, [handleSelectKpi, history, location]);

  const journeyNameByEmployeeId = useMemo(
    () => buildJourneyNameByEmployeeId(dayItems),
    [dayItems],
  );

  const replacementTarget = useMemo(
    () =>
      replacementTargets.find(
        candidate => candidate.attendanceRecordId === replacementTargetRecordId,
      ) ?? null,
    [replacementTargetRecordId, replacementTargets],
  );

  const replacementPickerUsers = useMemo(
    () =>
      replacementTargets.map(target => ({
        ...target.user,
        // Keep employee id on `id` for avatars / hydration; disambiguate rows by record.
        attendanceRecordId: target.attendanceRecordId,
        journeyName: target.journeyName || undefined,
      })),
    [replacementTargets],
  );

  const replacementStatusByRecordId = useMemo(() => {
    const statuses = new Map<string, AttendanceStatusDisplay>();
    replacementTargets.forEach(target => {
      statuses.set(target.attendanceRecordId, target.status);
    });
    return statuses;
  }, [replacementTargets]);

  const renderReplacementStatus = useMemo(
    () => createAttendanceStatusRenderer(intl, replacementStatusByRecordId),
    [intl, replacementStatusByRecordId],
  );

  const reportAbsencePickerUsers = useMemo(
    () =>
      reportAbsenceUsers.map(user => ({
        id: user.employeeId,
        attendanceRecordId: user.id,
        displayName: user.userDisplayName,
        email: user.userEmail ?? '',
        firstName: user.userFirstName ?? '',
        lastName: user.userLastName ?? '',
        idCard: user.userIdCard ?? '',
        journeyName: user.journeyName || undefined,
      })),
    [reportAbsenceUsers],
  );

  const reportAbsenceStatusByRecordId = useMemo(() => {
    const statuses = new Map<string, AttendanceStatusDisplay>();
    reportAbsenceUsers.forEach(user => {
      statuses.set(user.id, resolveUserRowAttendanceState(toUserStatusInput(user)));
    });
    return statuses;
  }, [reportAbsenceUsers]);

  const renderReportAbsenceStatus = useMemo(
    () => createAttendanceStatusRenderer(intl, reportAbsenceStatusByRecordId),
    [intl, reportAbsenceStatusByRecordId],
  );

  const markInitialUser = useMemo<MarkAttendanceUserRow | null>(
    () => (markInitialItem ? toMarkAttendanceUserRow(markInitialItem) : null),
    [markInitialItem],
  );

  const filterPanel = useMemo(
    () => (
      <WorkJourneyRealTimeFilters
        filters={filters}
        handleFilters={setFilters}
        onStructuralFilterChange={handleStructuralFilterChange}
        shiftOptions={shiftOptions}
        journeyOptions={journeyOptions}
        userOptions={userOptions}
        activeTab={activeTab}
      />
    ),
    [
      activeTab,
      filters,
      handleStructuralFilterChange,
      setFilters,
      shiftOptions,
      journeyOptions,
      userOptions,
    ],
  );

  const handleTabChange = useCallback(
    (key: string) => {
      if (
        Object.values(WORK_JOURNEY_REAL_TIME_TAB).includes(key as WorkJourneyRealTimeTab)
      ) {
        setActiveTab(key as WorkJourneyRealTimeTab);
      }
    },
    [setActiveTab],
  );

  const handleOpenMarkAttendance = useCallback(() => {
    setMarkInitialItem(null);
    setJourneyUsers(null);
    setMarkAttendanceOpen(true);
  }, []);

  const handleCloseMarkAttendance = useCallback(() => {
    setMarkAttendanceOpen(false);
    setMarkInitialItem(null);
    setJourneyUsers(null);
  }, []);

  const handleOpenReplacementList = useCallback(() => {
    setReplacementTargetRecordId(null);
    setReplaceListOpen(true);
  }, []);

  const handleCloseReplacementList = useCallback(() => {
    setReplaceListOpen(false);
  }, []);

  const handleSelectReplacementTarget = useCallback(
    (user: IWorkJourneyManagementUser) => {
      const recordId = getCoverPickerRowKey(user);
      const target = replacementTargets.find(
        candidate => candidate.attendanceRecordId === recordId,
      );
      if (!target) {
        return;
      }

      setReplaceListOpen(false);
      setReplacementTargetRecordId(target.attendanceRecordId);
    },
    [replacementTargets],
  );

  const handleCloseReplacementDrawer = useCallback(() => {
    setReplacementTargetRecordId(null);
  }, []);

  const handleReplacementRegistered = useCallback(async () => {
    handleCloseReplacementDrawer();
    refresh();
  }, [handleCloseReplacementDrawer, refresh]);

  const handleOpenReportAbsencePicker = useCallback(() => {
    setReportAbsenceUser(null);
    setReportAbsencePickerOpen(true);
  }, []);

  const handleCloseReportAbsencePicker = useCallback(() => {
    setReportAbsencePickerOpen(false);
  }, []);

  const handleSelectReportAbsenceUser = useCallback((user: AttendanceDashboardItem) => {
    setReportAbsencePickerOpen(false);
    setReportAbsenceUser(user);
  }, []);

  const handleSelectReportAbsenceUserByRecordId = useCallback(
    (attendanceRecordId: string) => {
      const user = reportAbsenceUsers.find(item => item.id === attendanceRecordId);
      if (user) {
        handleSelectReportAbsenceUser(user);
      }
    },
    [handleSelectReportAbsenceUser, reportAbsenceUsers],
  );

  const handleSelectReportAbsencePickerUser = useCallback(
    (user: IWorkJourneyManagementUser) => {
      handleSelectReportAbsenceUserByRecordId(getCoverPickerRowKey(user));
    },
    [handleSelectReportAbsenceUserByRecordId],
  );

  const handleCloseReportAbsence = useCallback(() => {
    setReportAbsenceUser(null);
  }, []);

  const handleSubmitReportAbsence = useCallback(
    async (payload: ReportAbsencePayload) => {
      if (!reportAbsenceUser) return;

      setReportAbsenceSubmitting(true);
      try {
        const saved = await handleReportAbsence(reportAbsenceUser, payload);
        if (!saved) return;

        handleCloseReportAbsence();
        refresh();
      } finally {
        setReportAbsenceSubmitting(false);
      }
    },
    [handleCloseReportAbsence, handleReportAbsence, refresh, reportAbsenceUser],
  );

  const reportAbsenceDate = useMemo(() => {
    if (!reportAbsenceUser) return '';
    const scheduledStartAtMs = DateUtil.getTimestampMs(reportAbsenceUser.scheduledStartAt);
    if (scheduledStartAtMs === null) return '';
    return DateUtil.toDateString(
      scheduledStartAtMs,
      'YYYY-MM-DD',
      DateUtil.getDefaultTimeZone(),
    );
  }, [reportAbsenceUser]);

  const reportAbsenceInitialExcuse = useMemo(() => {
    if (reportAbsenceUser?.leaveType === ATTENDANCE_LEAVE_TYPE.UNJUSTIFIED) {
      return 'unjustified_absence' as const;
    }
    return 'justified_absence' as const;
  }, [reportAbsenceUser]);

  const reportAbsenceMatrixUser = useMemo(
    () => (reportAbsenceUser ? toMatrixUser(reportAbsenceUser) : null),
    [reportAbsenceUser],
  );

  const markedJourneyUsersList = markedJourneyUsers ?? EMPTY_USER_ROWS;

  const isRequiresActionKpi = activeKpiKey === 'requiresAction';

  const actionButtons = useMemo((): FilterContainerActionButton[] => {
    const buttons: FilterContainerActionButton[] = [
      {
        label: intl.formatMessage({
          id: isFilterPanelOpen ? 'filterDropdown.closeFilters' : 'dashboard.tooltip.filter',
        }),
        iconName: 'filter_alt',
        type: 'secondary',
        action: toggleFilters,
        className: isFilterPanelOpen ? 'gx-border-primary' : '',
      },
    ];

    if (isRequiresActionKpi) {
      buttons.push({
        label: intl.formatMessage({ id: 'workJourney.realTime.fixUnmarked.action' }),
        iconName: 'build',
        type: 'primary',
        action: handleOpenGeneralFix,
        allowedRole: ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE,
      });
    }

    return buttons;
  }, [handleOpenGeneralFix, intl, isFilterPanelOpen, isRequiresActionKpi, toggleFilters]);

  const actionsMenuItems = useMemo((): MenuProps['items'] => {
    return [
      {
        key: REAL_TIME_ACTIONS.REPORT_ABSENCE,
        label: intl.formatMessage({ id: 'workJourney.realTime.reportAbsence.action' }),
        icon: menuItemIcon('person_remove'),
      },
      {
        key: REAL_TIME_ACTIONS.REPLACE_WORKER,
        label: intl.formatMessage({ id: 'workJourney.management.actions.replaceWorker' }),
        icon: menuItemIcon('swap_horiz'),
      },
      {
        key: REAL_TIME_ACTIONS.MARK_ATTENDANCE,
        label: intl.formatMessage({ id: 'workJourney.realTime.markAttendance.action' }),
        icon: menuItemIcon('how_to_reg'),
      },
    ];
  }, [intl]);

  const handleActionsMenuClick = useCallback<NonNullable<MenuProps['onClick']>>(
    ({ key }) => {
      if (key === REAL_TIME_ACTIONS.REPORT_ABSENCE) {
        handleOpenReportAbsencePicker();
        return;
      }
      if (key === REAL_TIME_ACTIONS.REPLACE_WORKER) {
        handleOpenReplacementList();
        return;
      }
      if (key === REAL_TIME_ACTIONS.MARK_ATTENDANCE) {
        handleOpenMarkAttendance();
      }
    },
    [handleOpenMarkAttendance, handleOpenReportAbsencePicker, handleOpenReplacementList],
  );

  const actionsDropdown = useMemo(
    () => (
      <ComponentBlocker allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE}>
        <Dropdown
          menu={{ items: actionsMenuItems, onClick: handleActionsMenuClick }}
          trigger={['click']}
        >
          <Button
            type={isRequiresActionKpi ? 'default' : 'primary'}
            className={`gx-mb-0 ${styles.headerActionButton}`}
          >
            <IntlMessages id="general.actions" />
            <DownOutlined />
          </Button>
        </Dropdown>
      </ComponentBlocker>
    ),
    [actionsMenuItems, handleActionsMenuClick, isRequiresActionKpi],
  );

  return (
    <>
      <SecondaryPanel.Panel
        title={intl.formatMessage({ id: 'dashboards.chart.filters' })}
        initialValues={INIT_WORK_JOURNEY_REAL_TIME_FILTERS as FilterValues}
      >
        {filterPanel}
      </SecondaryPanel.Panel>
      <BoxContainer fullContent fixed bottomBorder>
        <FilterContainer
          title={<Title.Header value={<IntlMessages id="workJourney.realTime.title" />} />}
          actionButtons={actionButtons}
          extraActionContent={actionsDropdown}
        />
      </BoxContainer>
      <FilterActiveTags.Container>
        <FilterActiveTags.Bar>
          <FilterActiveTags.ScrollArea>
            <FilterActiveTags onAddFilter={toggleFilters} />
          </FilterActiveTags.ScrollArea>
        </FilterActiveTags.Bar>
      </FilterActiveTags.Container>
      <BoxContainer fullContent>
        <SecondaryPanel.Content>
          <ListViewportLayout viewportOffset={160} sidePanelOpen={isFilterPanelOpen}>
            {error ? (
              <Alert
                className="gx-mb-3"
                type="error"
                showIcon
                message={intl.formatMessage({ id: 'workJourney.realTime.error.load' })}
                description={error}
              />
            ) : null}
            {isRangeTruncated ? (
              <Alert
                className="gx-mb-3"
                type="warning"
                showIcon
                message={intl.formatMessage({
                  id: 'workJourney.realTime.warning.truncated.title',
                })}
                description={intl.formatMessage(
                  { id: 'workJourney.realTime.warning.truncated.description' },
                  { max: rangeMaxRecords, total: rangeTotalAvailable },
                )}
              />
            ) : null}
            <WorkJourneyRealTimeKpiCards
              kpis={kpis}
              activeKpiKey={activeKpiKey}
              onSelectKpi={handleSelectKpi}
            />
            <Tabs
              activeKey={activeTab}
              items={REAL_TIME_TAB_ITEMS}
              onChange={handleTabChange}
            />
            {isLoaded && totalCount === 0 && !error ? (
              <Empty
                className="gx-my-4"
                description={intl.formatMessage({ id: 'workJourney.realTime.empty.description' })}
              />
            ) : activeTab === WORK_JOURNEY_REAL_TIME_TAB.BY_USER ? (
              <ResponsiveTable
                key={WORK_JOURNEY_REAL_TIME_TAB.BY_USER}
                columns={userColumns}
                dataSource={loading ? EMPTY_USER_ROWS : userTableData}
                loading={loading}
              />
            ) : (
              <ResponsiveTable
                key={WORK_JOURNEY_REAL_TIME_TAB.BY_JOURNEY}
                columns={journeyColumns}
                dataSource={loading ? EMPTY_JOURNEY_ROWS : journeyTableData}
                loading={loading}
              />
            )}
            {!loading && totalCount > 0 && (
              <div className="gx-d-flex gx-justify-content-center gx-mt-3">
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={totalCount}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                />
              </div>
            )}
          </ListViewportLayout>
        </SecondaryPanel.Content>
      </BoxContainer>
      <MarkAttendanceDrawer
        open={markAttendanceOpen}
        onClose={handleCloseMarkAttendance}
        initialUser={markInitialUser}
        initialUsers={journeyUsers}
        journeyNameByEmployeeId={journeyNameByEmployeeId}
        onStartJourneyInstead={handleStartJourneyFromPicker}
      />
      <MarkAttendanceConfirmModal
        open={startJourneyConfirmOpen}
        kind={startJourneyConfirmKind}
        userDisplayName={startJourneyUserDisplayName}
        markedAt={startJourneyMarkedAt}
        journeyDetails={startJourneyDetails}
        submitting={startJourneySubmitting}
        onConfirm={handleConfirmStartJourney}
        onCancel={handleCancelStartJourney}
        onMarkedAtChange={handleStartJourneyMarkedAtChange}
      />
      <MarkedJourneyUsersDrawer
        open={markedJourneyUsers !== null}
        users={markedJourneyUsersList}
        onClose={handleCloseMarkedJourneyUsers}
        onViewUserDetail={handleViewMarkedUserDetail}
        onMarkUser={item => {
          handleCloseMarkedJourneyUsers();
          handleMarkUser(item);
        }}
        onReplaceUser={item => {
          handleCloseMarkedJourneyUsers();
          handleReplaceUser(item);
        }}
        variant={markedJourneyUsersVariant}
      />
      <WorkJourneyCoverAbsenceDrawer
        open={replaceListOpen}
        onClose={handleCloseReplacementList}
        onReplace={handleSelectReplacementTarget}
        title={intl.formatMessage({ id: 'workJourney.management.actions.replaceWorker' })}
        actionLabel={intl.formatMessage({ id: 'workJourney.management.cover.absence.replace' })}
        sectionTitle={intl.formatMessage({
          id: 'workJourney.management.cover.absence.replaceSection',
        })}
        searchPlaceholderId="workJourney.management.cover.absence.searchPlaceholder"
        absentUsers={replacementPickerUsers}
        showConflict={false}
        renderStatus={renderReplacementStatus}
      />
      <WorkJourneyCoverAbsenceReplaceDrawer
        open={Boolean(replacementTarget)}
        onClose={handleCloseReplacementDrawer}
        onRegistered={handleReplacementRegistered}
        workJourneyConfigId={replacementTarget?.workJourneyConfigId}
        date={replacementTarget?.coverDate}
        absentUserId={replacementTarget?.user.id}
        journeyName={replacementTarget?.journeyName}
        currentMembers={replacementTarget?.currentMembers}
        requiredMembers={replacementTarget?.requiredMembers}
        absentUsers={replacementTarget?.absentUsers}
        replacedUser={replacementTarget?.user}
      />
      <WorkJourneyCoverAbsenceDrawer
        open={reportAbsencePickerOpen}
        onClose={handleCloseReportAbsencePicker}
        onReplace={handleSelectReportAbsencePickerUser}
        title={intl.formatMessage({ id: 'workJourney.realTime.reportAbsence.title' })}
        actionLabel={intl.formatMessage({ id: 'workJourney.realTime.reportAbsence.select' })}
        sectionTitle={intl.formatMessage({ id: 'workJourney.realTime.reportAbsence.selectUser' })}
        searchPlaceholderId="workJourney.realTime.reportAbsence.searchPlaceholder"
        width={560}
        absentUsers={reportAbsencePickerUsers}
        showConflict={false}
        renderStatus={renderReportAbsenceStatus}
      />
      <WorkJourneyMatrixAbsenceDrawer
        open={Boolean(reportAbsenceUser)}
        onClose={handleCloseReportAbsence}
        onSubmit={handleSubmitReportAbsence}
        user={reportAbsenceMatrixUser}
        initialExcuse={reportAbsenceInitialExcuse}
        initialStartDate={reportAbsenceDate}
        initialEndDate={reportAbsenceDate}
        hidePeriod
        submitting={reportAbsenceSubmitting}
      />
    </>
  );
};

export default WorkJourneyRealTime;
